import {
    Box,
    Flex,
    Heading,
    Icon,
    Icons,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeIconSizeVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { SafeHTML } from "components/safe-html";
import ImageWithSkeleton from "components/selection-components/image-with-skeleton/image-with-skeleton";
import { useLocalizedValue } from "helpers/use-localized-value";
import { useTranslation } from "react-i18next";
import { urlBuilder } from "search-params/url-builder";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { ExperienceSuggestion } from "utils/experience-suggestions";

const THUMBNAIL_SIZE = "72px";

interface SuggestionRowProps {
    suggestion: ExperienceSuggestion;
    onSelect: (experienceId: number) => void;
}

function SuggestionRow({ suggestion, onSelect }: SuggestionRowProps) {
    const name = useLocalizedValue(suggestion.name);
    const description = useLocalizedValue(suggestion.description);

    const imageReference = suggestion.imageReference1x1 || suggestion.imageReference16x9;
    const imageUrl = imageReference
        ? urlBuilder(`${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${imageReference}`, {
              AspectRatio: ImageServiceAspectRatio.OneByOne,
              Type: "webp",
          })
        : "";

    return (
        <Box
            as="button"
            type="button"
            w="full"
            textAlign="left"
            borderRadius={ThemeRadiusVariable.Small}
            onClick={() => onSelect(suggestion.id)}
            // @ts-expect-error _focusVisible is not typed in casper Box
            _focusVisible={{ boxShadow: "var(--theme-shadows-outline)" }}
        >
            <Flex align="center" gap={ThemeSpaceVariable.Medium}>
                {imageUrl && (
                    <ImageWithSkeleton
                        src={imageUrl}
                        alt={name}
                        w={THUMBNAIL_SIZE}
                        h={THUMBNAIL_SIZE}
                        skeletonWidth={THUMBNAIL_SIZE}
                        skeletonHeight={THUMBNAIL_SIZE}
                        objectFit="cover"
                        borderRadius={ThemeRadiusVariable.Small}
                    />
                )}
                <Stack spacing={ThemeSpaceVariable.None} flex={1} minW={0}>
                    <Text
                        noOfLines={2}
                        fontWeight="medium"
                        color={ThemeColorVariable.OnBackgroundHeader}
                    >
                        {name}
                    </Text>
                    {description && (
                        <Text
                            noOfLines={2}
                            fontSize={ThemeFontSizeVariable.Small}
                            color={ThemeColorVariable.OnBackgroundSubdued}
                        >
                            <SafeHTML
                                element="span"
                                options={{ allowedTags: ["br"], allowedSchemes: [] }}
                                html={description}
                            />
                        </Text>
                    )}
                </Stack>
                <Icon
                    icon={Icons.ChevronRight}
                    size={ThemeIconSizeVariable.Medium}
                    color={ThemeColorVariable.OnBackgroundSubdued}
                />
            </Flex>
        </Box>
    );
}

interface ExperienceSuggestionsProps {
    suggestions: ExperienceSuggestion[];
    onSelect?: (experienceId: number) => void;
}

function ExperienceSuggestions({ suggestions, onSelect }: ExperienceSuggestionsProps) {
    const { t } = useTranslation();

    if (suggestions.length === 0 || !onSelect) return null;

    return (
        <Box
            bg={ThemeColorVariable.Background}
            mx={`calc(${ThemeSpaceVariable.Medium} * -1)`}
            mt={ThemeSpaceVariable.Large}
            px={ThemeSpaceVariable.Medium}
            pt={ThemeSpaceVariable.Medium}
            pb={ThemeSpaceVariable.Large}
        >
            <Heading
                as="h3"
                fontFamily={ThemeFontVariable.SubHeader}
                fontWeight="medium"
                fontSize={ThemeFontSizeVariable.Medium}
                color={ThemeColorVariable.OnBackgroundSubdued}
                pb={ThemeSpaceVariable.Medium}
            >
                {t("experienceView.suggestions.title")}
            </Heading>
            <Stack spacing={ThemeSpaceVariable.Medium}>
                {suggestions.map((suggestion) => (
                    <SuggestionRow
                        key={suggestion.id}
                        suggestion={suggestion}
                        onSelect={onSelect}
                    />
                ))}
            </Stack>
        </Box>
    );
}

export { ExperienceSuggestions };
